import React from 'react';
import { Button } from '@/components/ui/button';
import { ExternalLink, Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PlaceDetailCardProps {
  title: string;
  /** Icon prop kept for backward compatibility — no longer rendered as a bubble. */
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * Clean, Airbnb / Notion-style content panel.
 * - No icon bubble in the title row
 * - Subtle card surface, no hover shadow, no gradient backgrounds
 * - Generous spacing, typographic hierarchy
 */
export const PlaceDetailCard: React.FC<PlaceDetailCardProps> = ({
  title,
  children,
  className = '',
}) => (
  <section
    className={cn(
      'bg-card rounded-2xl border border-border/50 p-5 sm:p-6',
      className
    )}
  >
    <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3 sm:mb-4 tracking-tight">
      {title}
    </h2>
    <div className="text-sm text-foreground/80 leading-relaxed">{children}</div>
  </section>
);

export const RatingDisplay: React.FC<{
  rating: number | string | null;
}> = ({ rating }) => {
  if (!rating) return null;
  const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
  const fullStars = Math.floor(numRating);
  const hasHalfStar = numRating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-2">
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < fullStars
                ? 'text-yellow-400 fill-yellow-400'
                : i === fullStars && hasHalfStar
                  ? 'text-yellow-400 fill-yellow-400/50'
                  : 'text-gray-300'
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-medium">{numRating}/5</span>
    </div>
  );
};

export const OfficialWebsiteButton: React.FC<{
  url: string;
  label?: string;
}> = ({ url, label = 'Official Website' }) => {
  const displayUrl = url.replace(/^https?:\/\//, '');
  const truncatedUrl =
    displayUrl.length > 25 ? displayUrl.substring(0, 25) + '...' : displayUrl;
  return (
    <Button
      variant="outline"
      className="w-full justify-start gap-3 h-12 border-primary/20 hover:border-primary/40 hover:bg-primary/5"
      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
    >
      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0" />
      <div className="flex flex-col items-start min-w-0 flex-1">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground truncate w-full">
          {truncatedUrl}
        </span>
      </div>
    </Button>
  );
};

export const AddressBadge: React.FC<{
  address: string;
  label?: string;
}> = ({ address, label = 'Address' }) => {
  const truncatedAddress =
    address.length > 40 ? address.substring(0, 40) + '...' : address;
  return (
    <Button
      variant="outline"
      className="w-full justify-start gap-3 h-12 border-dashed hover:border-solid hover:bg-accent/50"
      onClick={() => {
        const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
        window.open(mapsUrl, '_blank', 'noopener,noreferrer');
      }}
    >
      <span className="text-base flex-shrink-0">📍</span>
      <div className="flex flex-col items-start min-w-0 flex-1">
        <span className="font-medium">{label}</span>
        <span className="text-xs text-muted-foreground truncate w-full">
          {truncatedAddress}
        </span>
      </div>
    </Button>
  );
};
